import React, {useCallback, useContext, useEffect, useState} from 'react';
import {Button, Text, Modal, Dialogue, LoadingSpinner, Icon} from '@albert-io/atomic';
import type {ButtonVariant} from '@albert-io/atomic/atoms/Button/Button.react';
import {CleverCourseSyncModelV1} from 'resources/GeneratedModels/CleverCourseSync/CleverCourseSyncModel.v1';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {invalidateClassroomsQueries} from 'client/Classrooms/Teacher/NewClassroomModal/newClassroomModal.utils';
import {callAction} from 'client/framework';
import sessionLifecycleActions from 'client/Session/SessionLifecycleActions';
import moment from 'moment';

import type {CleverSyncResults} from './util';
import {getUserQuery} from './util';
import type {CleverSyncAction} from './CleverSyncProvider';
import {CleverSyncDispatch, CleverSyncContext} from './CleverSyncProvider';
import SyncResults from './SyncResults';
import './clever-sync.scss';

const RETRY_INTERVAL = 2000; // 2000ms

const getRunningJobQuery = (id: string) =>
  resource('clever_course_sync_v1').mandarkEndpoint(['clever_course_syncs_v1', id]);

export const createSyncJob = async (
  onStart: () => void,
  completeJob: (jobResult: CleverSyncResults) => void
) => {
  onStart();
  const sync = new CleverCourseSyncModelV1().addRelationship({
    type: 'user_v2',
    relation: masqueradeStore.getUserIdByMasqueradeState()
  });
  try {
    const result = await sync.save();
    const jobId = result.toJS().id;
    // Returned so a caller can clearInterval on unmount (the poll starts after this await, so the
    // cleanup can only be handed back asynchronously). Legacy callers ignore it, as before.
    return pollCleverCourseSync(jobId, completeJob);
  } catch (_e: any) {
    completeJob({success: false, message: 'Unknown error'});
    return undefined;
  }
};

export const pollCleverCourseSync = (
  jobId: string,
  completeJob: (jobResult: CleverSyncResults) => void
) => {
  const interval = setInterval(async () => {
    try {
      const runningJob = (await getRunningJobQuery(
        jobId
      ).getResourcePromise()) as CleverCourseSyncModelV1;
      getRunningJobQuery(jobId).invalidateInterest();
      if (runningJob.getStatus() === 'finished') {
        clearInterval(interval);
        const results: CleverSyncResults = runningJob.getResults().toJS();
        completeJob(results);
      }
    } catch (_e) {
      // A transient status-fetch failure must not abort the sync: the job keeps running
      // server-side and the next tick re-checks. Swallow it so a rejected poll doesn't
      // surface as an unhandled rejection.
    }
  }, RETRY_INTERVAL);
  // Returned so an effect caller can clearInterval on unmount and stop polling a job it no
  // longer displays — otherwise the interval leaks and dispatches into an unmounted component.
  return () => clearInterval(interval);
};

const onCloseModal = (dispatch: (value: CleverSyncAction) => void) => {
  getUserQuery().invalidateInterest();
  invalidateClassroomsQueries();
  dispatch({type: 'closeModal'});
};

const SyncIcon = () => <Icon className='h-x4 w-x4' icon='arrows-rotate' iconStyle='light' />;

const ErrorIcon = () => (
  <Icon color='negative' className='h-x4 w-x4' icon='triangle-exclamation' iconStyle='light' />
);

const WarningIcon = () => (
  <Icon color='warning' className='h-x4 w-x4' icon='circle-exclamation-check' iconStyle='light' />
);

const ContactText = () => (
  <Text>
    Reach out to <strong>hello@albert.io</strong> if you need assistance.
  </Text>
);

const CloseModalButton = () => {
  const dispatch = useContext(CleverSyncDispatch);

  return (
    <Button
      variant='outlined'
      onClick={() => {
        onCloseModal(dispatch);
      }}
      className='w-full'
    >
      Dismiss
    </Button>
  );
};

const InlineButton = () => {
  const state = useContext(CleverSyncContext);
  const dispatch = useContext(CleverSyncDispatch);

  const onComplete = useCallback(
    (syncResults: CleverSyncResults) => {
      dispatch({
        type: 'setLoading',
        value: false
      });
      dispatch({
        type: 'setSyncResults',
        syncResults
      });
      getUserQuery().invalidateInterest();
      invalidateClassroomsQueries();
      if (syncResults.success && syncResults.results.some((r) => r.success)) {
        dispatch({
          type: 'setSyncDate',
          date: moment()
        });
      }
      if (
        !syncResults.success ||
        !syncResults.results.every((r) => r.success) ||
        syncResults.results.some((r) => r.success && r.status === 'archived')
      ) {
        dispatch({
          type: 'openModal'
        });
      }
    },
    [dispatch]
  );
  const onStart = useCallback(() => {
    dispatch({
      type: 'setLoading',
      value: true
    });
  }, [dispatch]);

  useEffect(() => {
    if (state.currentlyInProgressSyncId) {
      onStart();
      pollCleverCourseSync(state.currentlyInProgressSyncId, onComplete);
      dispatch({type: 'setCurrentlyInProgressSyncId'});
    }
  }, [onComplete, onStart, state.currentlyInProgressSyncId, dispatch]);

  const buttonMessage = state.lastSyncDate ? 'Sync With Clever' : 'Import & Sync With Clever';

  return (
    <Button
      variant='outlined'
      className={`mr-x1 ${state.loading && `pointer-events-none`}`}
      onClick={() => {
        if (state.userCleverId && state.userCleverDistrictId) {
          createSyncJob(onStart, onComplete);
        } else {
          dispatch({type: 'openLogoutModal'});
        }
      }}
    >
      {state.loading ? (
        <>
          <Icon spin icon='sync' className='mr-x1' /> Syncing...
        </>
      ) : (
        buttonMessage
      )}
    </Button>
  );
};

const getSyncStatus = (
  syncResults?: CleverSyncResults
): 'all' | 'some' | 'none' | 'error' | 'empty' | null => {
  if (!syncResults) {
    return null;
  }
  if (syncResults.success === false) {
    return 'error';
  }
  if (syncResults.results.length === 0) {
    return 'empty';
  }
  if (syncResults.results.every((r) => r.success)) {
    return 'all';
  }
  if (syncResults.results.every((r) => !r.success)) {
    return 'none';
  }
  return 'some';
};

export const CleverSyncModal = () => {
  const state = useContext(CleverSyncContext);
  const dispatch = useContext(CleverSyncDispatch);
  const onComplete = (syncResults: CleverSyncResults) => {
    dispatch({
      type: 'setSyncResults',
      syncResults
    });
    dispatch({
      type: 'setLoading',
      value: false
    });
    if (syncResults.success && syncResults.results.some((r) => r.success)) {
      dispatch({
        type: 'setSyncDate',
        date: moment()
      });
    }
  };
  const onStart = () => {
    dispatch({
      type: 'setLoading',
      value: true
    });
  };

  const syncStatus = getSyncStatus(state.syncResults);
  const specificError = !state.syncResults?.success && state.syncResults?.message;

  return (
    <Modal
      isLoading={false}
      closeModalPredicate={() => false}
      focusCloseButton={false}
      ariaLabel='clever sync modal'
      handleClose={() => {
        onCloseModal(dispatch);
      }}
    >
      {() => (
        <>
          {state.loading && (
            <Dialogue inModal className='clever-sync' hideCloseBtn size='s'>
              <Dialogue.Body className='flex flex-col items-center gap-x2 text-center'>
                <LoadingSpinner size={3} className='content-secondary' />
                <Text>Sync in progress...</Text>
              </Dialogue.Body>
            </Dialogue>
          )}
          {syncStatus === 'all' && (
            <Dialogue inModal className='clever-sync' hideCloseBtn size='l'>
              <Dialogue.Body className='flex flex-col items-center gap-x4 text-center'>
                <Icon color='positive' className='h-x4 w-x4' icon='check' />
                <Text bold>Your Clever rosters and classrooms were all imported successfully!</Text>
                <SyncResults className='sync-results' syncResults={state.syncResults} />
                <CloseModalButton />
              </Dialogue.Body>
            </Dialogue>
          )}
          {syncStatus === 'some' && (
            <Dialogue inModal className='clever-sync' hideCloseBtn size='l'>
              <Dialogue.Body className='flex flex-col items-center gap-x2 text-center'>
                <WarningIcon />
                <Text bold>Review changes from Clever:</Text>
                <SyncResults className='sync-results' syncResults={state.syncResults} />
                <ContactText />
                <CloseModalButton />
              </Dialogue.Body>
            </Dialogue>
          )}
          {syncStatus === 'none' && (
            <Dialogue inModal className='clever-sync' hideCloseBtn size='l'>
              <Dialogue.Body className='flex flex-col items-center gap-x2 text-center'>
                <ErrorIcon />
                <Text bold>Import failed.</Text>
                <SyncResults className='sync-results' syncResults={state.syncResults} />
                <ContactText />
                <CloseModalButton />
              </Dialogue.Body>
            </Dialogue>
          )}
          {syncStatus === 'error' && specificError === 'User not found in Clever' && (
            <Dialogue inModal className='clever-sync' hideCloseBtn size='s'>
              <Dialogue.Body className='flex flex-col items-center gap-x2 text-center'>
                <ErrorIcon />
                <Text bold>Incorrect Clever Sharing Rules</Text>
                <Text>
                  Your account is not included on your district&apos;s Clever sharing rules for the
                  &quot;Albert (Rostering)&quot; app.
                </Text>
                <Text>
                  Please ask your district or school tech admin to add you to their sharing rules
                  for the &quot;Albert (Rostering)&quot; app.
                </Text>
                <CloseModalButton />
              </Dialogue.Body>
            </Dialogue>
          )}
          {syncStatus === 'error' && specificError !== 'User not found in Clever' && (
            <Dialogue inModal className='clever-sync' hideCloseBtn size='s'>
              <Dialogue.Body className='flex flex-col items-center gap-x3 text-center'>
                <ErrorIcon />
                <Text bold>Something went wrong.</Text>
                <Text>Your Clever classrooms were not imported.</Text>
                <ContactText />
                <CloseModalButton />
              </Dialogue.Body>
            </Dialogue>
          )}
          {syncStatus === 'empty' && (
            <Dialogue inModal className='clever-sync' hideCloseBtn size='s'>
              <Dialogue.Body className='flex flex-col items-center gap-x2 text-center'>
                <WarningIcon />
                <Text bold>No classes to import.</Text>
                <Text>
                  This may be because your district is not sharing your Clever section data.
                </Text>
                <ContactText />
                <CloseModalButton />
              </Dialogue.Body>
            </Dialogue>
          )}
          {!syncStatus && !state.loading && (
            <Dialogue inModal hideCloseBtn className='clever-sync' size='s'>
              <Dialogue.Body className='flex flex-col items-center gap-x4 text-center'>
                <SyncIcon />
                <Text>
                  Your entire roster and classrooms will be imported from Clever. Once imported,
                  you&#39;ll be able to sync any changes made.
                </Text>
                <div className='flex w-full flex-col gap-x1'>
                  <Button
                    onClick={() => {
                      createSyncJob(onStart, onComplete);
                    }}
                  >
                    Import &amp; Sync With Clever
                  </Button>
                  <Button
                    variant='outlined'
                    onClick={() => {
                      onCloseModal(dispatch);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </Dialogue.Body>
            </Dialogue>
          )}
        </>
      )}
    </Modal>
  );
};

export const CleverSyncButtonWithModal = ({
  variant,
  className
}: {
  variant?: ButtonVariant;
} & PropsWithClassNameOptional) => {
  const dispatch = useContext(CleverSyncDispatch);
  const state = useContext(CleverSyncContext);

  return (
    <>
      <Button
        className={className}
        variant={variant}
        onClick={() => {
          if (state.userCleverId && state.userCleverDistrictId) {
            dispatch({type: 'openModal'});
          } else {
            dispatch({type: 'openLogoutModal'});
          }
        }}
      >
        Import &amp; Sync With Clever
      </Button>
    </>
  );
};

export const CleverNotLoggedInModal = () => {
  const dispatch = useContext(CleverSyncDispatch);

  const [loggingOut, setLoggingOut] = useState(false);

  return (
    <Modal
      isLoading={false}
      closeModalPredicate={() => false}
      focusCloseButton={false}
      ariaLabel='clever not logged in modal'
      handleClose={() => {
        dispatch({type: 'closeLogoutModal'});
      }}
    >
      {() => (
        <Dialogue inModal size='s' hideCloseBtn>
          <Dialogue.Body align='center'>
            <Text>
              Before you can import and sync, you&apos;ll need to log out of Albert and log back in
              with Clever.
            </Text>
            <div className='mt-x5 flex flex-col gap-x1'>
              <Button
                className={loggingOut ? 'pointer-events-none' : ''}
                onClick={() => {
                  setLoggingOut(true);
                  // `payload`/`requestId` are optional at runtime (default to
                  // undefined); passed explicitly because callAction's signature
                  // in client/framework does not yet mark them optional.
                  callAction(sessionLifecycleActions.END_SESSION);
                }}
              >
                {loggingOut ? 'Logging out...' : 'Log Out Of Albert'}
              </Button>
              <Button
                variant='outlined'
                onClick={() => {
                  dispatch({type: 'closeLogoutModal'});
                }}
              >
                Cancel
              </Button>
            </div>
          </Dialogue.Body>
        </Dialogue>
      )}
    </Modal>
  );
};

export const CleverSyncButton = ({variant}: {variant?: ButtonVariant}) => {
  const state = useContext(CleverSyncContext);

  let button: any = null;
  if (!state.lastSyncDate) {
    button = <CleverSyncButtonWithModal className='mr-x1' variant={variant} />;
  } else {
    button = (
      <>
        {state.lastSyncDate && !state.loading && (
          <Text color='secondary' size='s' className='mr-x2'>
            Last successful sync {state.lastSyncDate.format('M/D/YYYY')} at{' '}
            {state.lastSyncDate.format('LT')}
          </Text>
        )}
        <InlineButton />
      </>
    );
  }
  return (
    <>
      {button}
      {state.isModalOpen && <CleverSyncModal />}
      {state.isLogoutModalOpen && <CleverNotLoggedInModal />}
    </>
  );
};
